import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  FluentProvider,
  createDarkTheme,
  createLightTheme,
  type BrandVariants,
  type Theme,
} from '@fluentui/react-components';
import { useIsAuthenticated } from '@azure/msal-react';
import { comlineBrand, generateBrandRamp } from './brandRamp';
import { createPipTheme, PIP_FONT } from './pipTheme';
import { initTeams, registerThemeChange, type TeamsTheme } from '../teams/teamsContext';
import { useApi } from '../api/useApi';
import type { BrandingDto } from '../api/types';

export type ThemePref = 'light' | 'dark' | 'pip';

const THEME_KEY = 'm365adminbox-theme';

interface BrandingState {
  appName: string;
  logoUrl?: string;
  branding?: BrandingDto;
  reload: () => void;
  themePref: ThemePref;
  setThemePref: (pref: ThemePref) => void;
}

const FONT_STACKS: Record<string, string> = {
  Outfit: "'Outfit', 'Segoe UI', system-ui, sans-serif",
  'Segoe UI': "'Segoe UI', system-ui, -apple-system, BlinkMacSystemFont, sans-serif",
};

function fontStack(font?: string): string {
  return FONT_STACKS[font ?? 'Segoe UI'] ?? FONT_STACKS['Segoe UI'];
}

const BrandingContext = createContext<BrandingState>({
  appName: 'M365 Adminbox',
  reload: () => {},
  themePref: 'light',
  setThemePref: () => {},
});

// eslint-disable-next-line react-refresh/only-export-components
export const useBranding = (): BrandingState => useContext(BrandingContext);

function toMode(theme: TeamsTheme): 'light' | 'dark' {
  return theme === 'dark' || theme === 'contrast' ? 'dark' : 'light';
}

function storedPref(): ThemePref | null {
  const v = localStorage.getItem(THEME_KEY);
  return v === 'light' || v === 'dark' || v === 'pip' ? v : null;
}

export function BrandingProvider({ children }: { children: ReactNode }) {
  const isAuthenticated = useIsAuthenticated();
  const api = useApi();
  const [brand, setBrand] = useState<BrandVariants>(comlineBrand);
  const [themePref, setThemePrefState] = useState<ThemePref>(() => storedPref() ?? 'light');
  const [appName, setAppName] = useState('M365 Adminbox');
  const [logoUrl, setLogoUrl] = useState<string | undefined>();
  const [branding, setBranding] = useState<BrandingDto | undefined>();

  const setThemePref = useCallback((pref: ThemePref) => {
    setThemePrefState(pref);
    localStorage.setItem(THEME_KEY, pref);
  }, []);

  const loadBranding = useCallback(() => {
    api
      .getBranding()
      .then((b) => {
        setAppName(b.appName || 'M365 Adminbox');
        setLogoUrl(b.logoUrl);
        setBranding(b);
        if (b.primaryColor) setBrand(generateBrandRamp(b.primaryColor));
      })
      .catch(() => {
        /* Standard beibehalten */
      });
  }, [api]);

  // Teams-Einbettung: nur als initialer Default, wenn der Nutzer nichts gewählt hat.
  useEffect(() => {
    let active = true;
    initTeams().then(({ theme }) => {
      if (!active) return;
      if (!storedPref()) setThemePrefState(toMode(theme));
      registerThemeChange((t) => {
        if (!storedPref()) setThemePrefState(toMode(t));
      });
    });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (isAuthenticated) loadBranding();
  }, [isAuthenticated, loadBranding]);

  const fontFamilyBase = themePref === 'pip' ? PIP_FONT : fontStack(branding?.fontFamily);

  useEffect(() => {
    document.documentElement.style.setProperty('--app-font', fontFamilyBase);
  }, [fontFamilyBase]);

  // CRT-Overlay (Scanlines/Glow/Vignette) hängt an <html data-pip="on">.
  useEffect(() => {
    const root = document.documentElement;
    if (themePref === 'pip') root.setAttribute('data-pip', 'on');
    else root.removeAttribute('data-pip');
  }, [themePref]);

  const theme: Theme = useMemo(() => {
    if (themePref === 'pip') return createPipTheme();
    return {
      ...(themePref === 'dark' ? createDarkTheme(brand) : createLightTheme(brand)),
      fontFamilyBase,
    };
  }, [brand, themePref, fontFamilyBase]);

  const value = useMemo<BrandingState>(
    () => ({ appName, logoUrl, branding, reload: loadBranding, themePref, setThemePref }),
    [appName, logoUrl, branding, loadBranding, themePref, setThemePref],
  );

  return (
    <BrandingContext.Provider value={value}>
      <FluentProvider theme={theme} style={{ height: '100%' }}>
        {children}
      </FluentProvider>
    </BrandingContext.Provider>
  );
}
